import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteAashtoware } from '../hooks/api/use-aashtoware.hook';
import { AashtowareApiModel } from '../../../types/models/aashtoware.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchAashtoware } from '../api/aashtoware.api';

export const Aashtoware = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteAashtoware, { loading: deletingAashtoware, error: deleteError }] = useDeleteAashtoware();

  const columns: ColDef<AashtowareApiModel>[] = [
    {
      field: 'code',
      colId: 'aashtowareCode',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    { field: 'name', sortable: true, filter: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      field: 'specBook',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'description',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    { field: 'unit', sortable: true, filter: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      field: 'unitSystem',
      sortable: true,
      filter: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<AashtowareApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteAashtoware({ aashtowareId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Aashtoware',
                message: `Are you sure you want to delete aashtoware "${node.data?.name}"?`,
                error: deleteError?.response?.data.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getAashtoware: Parameters<typeof InfniteScrollingGrid<AashtowareApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchAashtoware({
      ...filterQuery,
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Aashtoware'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Aashtoware</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Aashtoware
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingAashtoware} columnDefs={columns} onGetRows={getAashtoware} />
        </Box>
      </Box>
    </Box>
  );
};
